import { Parser, ProcessNodeDefinitions } from 'html-to-react';
import striptags from 'striptags';
import { Button } from '@bridebook/aura';
import { ArrowRight } from '@bridebook/aura/src/icons';
import type { Market } from '@bridebook/toolbox/src/gazetteer';
import { Box } from '@bridebook/ui';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { formatCloudinaryUrl } from '@bridebook/web/app-shared/lib/articles/utils';
import type { DataNode, Element, Node, NodeWithChildren, TextNode } from '../../../types/dom';
import { processArticleUrls } from '../../lib/articles/process-article-urls';
import React from 'react';
import { alwaysTransformedBanners, ctaBannerMap } from './CTABanners/cta-banner-map';
import { FIRST_DANCE_TRACKER_SRC } from './first-dance-tracker/constants';
import { FirstDanceTracker } from './first-dance-tracker/first-dance-tracker';

const htmlToReactParser = new Parser();

const processNodeDefinitions = new ProcessNodeDefinitions(React);

export type TExternalLinkClickCallback = (options: {
  externalUrl: string;
  ctaText: string;
}) => void;

interface IRenderHTMLArgs {
  htmlContent: string | undefined;
  isCordova?: boolean;
  market: Market;
  externalLinkClickCallback?: TExternalLinkClickCallback;
  enableTransforms?: boolean; // render CTA banner placeholders (alwaysTransformedBanners render regardless)
  styleTable?: boolean;
}

export const renderHtml = ({
  htmlContent,
  isCordova = false,
  market,
  externalLinkClickCallback,
  enableTransforms,
  styleTable,
}: IRenderHTMLArgs): null | any => {
  if (!htmlContent?.trim()) {
    return null;
  }

  const processingInstructions = [
    {
      shouldProcessNode: (node: Element) => isInternalLink(node),
      processNode: (node: Element) => convertAnchorToLink(node, { isExternal: false }),
    },
    {
      shouldProcessNode: (node: Element) => isExternalLink(node),
      processNode: (node: Element) =>
        convertAnchorToLink(node, { isExternal: true, externalLinkClickCallback }),
    },
    {
      shouldProcessNode: (node: Element) => isComponentMarker(node),
      processNode: (node: Element) => convertToComponent(node, enableTransforms),
    },
    {
      shouldProcessNode: (node: Element) => isFirstDanceTrackerMarker(node),
      processNode: (node: Element) => convertToFirstDanceTracker(node),
    },
    ...(styleTable
      ? [
          {
            shouldProcessNode: isBBTable,
            processNode: convertBBTable,
          },
        ]
      : []),
    {
      shouldProcessNode: () => true,
      processNode: processNodeDefinitions.processDefaultNode,
    },
  ];

  const contentMarkup = getContentMarkup(htmlContent, isCordova, market);
  return htmlToReactParser.parseWithInstructions(contentMarkup, () => true, processingInstructions);
};

const getContentMarkup = (content: string, isCordova: boolean, market: Market): string => {
  const clean: string = striptags(
    content,
    '<a><strong><img><figure><span><figcaption><p><b><ul><ol><li><table><thead><tbody><tr><td><th><em><h2><h3><h4><i><iframe><div>',
  );

  const withTargets = processArticleUrls(clean, isCordova, market);

  return withTargets
    .replace(/srcset="[^"]*"/g, '')
    .replace(
      /src="(.*?)"/g,
      (_a, match) =>
        `src="${formatCloudinaryUrl(match)}" srcset="${formatCloudinaryUrl(
          match,
          '1',
        )} 1x, ${formatCloudinaryUrl(match, '2')} 2x"`,
    );
};

const isInternalLink = (domNode: Element) => {
  const { name, children, attribs } = domNode;
  if (domNode.type === 'tag') {
    return attribs && !attribs.rel && name && children && name === 'a';
  }
  return false;
};

const isExternalLink = (domNode: Element) => {
  const { name, children, attribs } = domNode;
  if (domNode.type === 'tag') {
    return attribs && attribs.rel && attribs.rel === 'nofollow' && children && name && name === 'a';
  }

  return false;
};

const isComponentMarker = (domNode: Element) => {
  const child = domNode?.children?.[0] as unknown as DataNode;

  return Object.keys(ctaBannerMap).includes(child?.data);
};

const isFirstDanceTrackerMarker = (domNode: Element) => {
  const child = domNode?.children?.[0] as unknown as DataNode;

  return Object.keys(FIRST_DANCE_TRACKER_SRC).includes(child?.data);
};

const isBBTable = (domNode: Element): boolean => domNode.type === 'tag' && domNode.name === 'table';

const convertBBTable = (_node: Element, children: React.ReactNode[], index: number) => (
  <div key={index} className="my-6 overflow-x-auto rounded-lg border border-space-15">
    <table className="bb-table-wp min-w-full border-collapse text-left [&_td]:px-6 [&_td]:py-4 [&_td]:text-sm [&_td]:whitespace-nowrap [&_td]:text-space-60 [&_td:first-child]:font-semibold [&_td:first-child]:text-space [&_th]:px-6 [&_th]:py-4 [&_th]:text-sm [&_th]:font-bold [&_th]:tracking-wide [&_th]:whitespace-nowrap [&_th]:text-space [&_thead]:bg-indigo-crush-20 [&_thead>tr]:border-t-0 [&_tr]:border-t [&_tr]:border-space-15">
      {children}
    </table>
  </div>
);

// Type guard function to identify Text node
const isTextNode = (node: Node): node is TextNode => node.type === 'text';

const renderChildrenText = (children: NodeWithChildren[]): string =>
  children.reduce((result, child) => {
    if (isTextNode(child)) {
      result += child.data;
    } else if (child.children?.length > 0) {
      result += renderChildrenText(child.children as NodeWithChildren[]);
    }
    return result;
  }, '');

const ANCHOR_BUTTON_VARIANTS: Record<string, React.ComponentProps<typeof Button>['variant']> = {
  'bb-primary': 'primary',
  'bb-secondary': 'secondary',
  'bb-text-link': 'ghost',
};

const getButtonVariant = (classAttr: string | undefined) =>
  classAttr
    ?.split(/\s+/)
    .map((className) => ANCHOR_BUTTON_VARIANTS[className])
    .find(Boolean);

const convertAnchorToLink = (
  domNode: Element,
  {
    isExternal,
    externalLinkClickCallback,
  }: {
    isExternal: boolean;
    externalLinkClickCallback?: TExternalLinkClickCallback;
  },
) => {
  const {
    attribs: { rel, target, href, class: className },
    children,
  } = domNode;
  const firstChild = domNode.firstChild;
  const tag = firstChild as Element;

  const commonAttribs = { rel, target, href };
  const buttonVariant = getButtonVariant(className);

  if (tag && tag.name === 'img') {
    return (
      <Box
        display="block"
        {...commonAttribs}
        as="a"
        onClick={
          isExternal
            ? () =>
                externalLinkClickCallback?.({
                  externalUrl: commonAttribs.href,
                  ctaText: tag.attribs.alt,
                })
            : undefined
        }>
        <img className={tag.attribs.class} src={tag.attribs.src} alt={tag.attribs.alt} />
      </Box>
    );
  }

  if (children?.length > 0) {
    const linkText = renderChildrenText(children as NodeWithChildren[]);
    const key = (firstChild as TextNode).data;
    const handleExternalLinkClick = () =>
      externalLinkClickCallback?.({
        externalUrl: commonAttribs.href,
        ctaText: linkText,
      });

    if (isExternal) {
      if (buttonVariant) {
        return (
          <Button
            key={key}
            asChild
            variant={buttonVariant}
            className="m-1 w-fit"
            iconRight={buttonVariant === 'ghost' ? ArrowRight : undefined}>
            <a {...commonAttribs} data-aura-button onClick={handleExternalLinkClick}>
              {linkText}
            </a>
          </Button>
        );
      }
      return (
        <Box key={key} {...commonAttribs} as="a" display="inline" onClick={handleExternalLinkClick}>
          {linkText}
        </Box>
      );
    }

    if (buttonVariant) {
      return (
        <Button
          key={key}
          asChild
          variant={buttonVariant}
          className="m-1 w-fit"
          iconRight={buttonVariant === 'ghost' ? ArrowRight : undefined}>
          <Link {...commonAttribs} data-aura-button prefetch={false}>
            {linkText}
          </Link>
        </Button>
      );
    }

    return (
      <Link {...commonAttribs} key={key} prefetch={false}>
        {linkText}
      </Link>
    );
  }

  return null;
};

const convertToComponent = (domNode: Element, enableTransforms?: boolean) => {
  const marker = (domNode?.children?.[0] as unknown as DataNode)?.data ?? '';
  const BannerComponent = ctaBannerMap[marker];

  if (!BannerComponent || (!enableTransforms && !alwaysTransformedBanners.includes(marker)))
    return null;

  return (
    <div className="mb-6">
      <BannerComponent />
    </div>
  );
};

const convertToFirstDanceTracker = (domNode: Element) => {
  const childData = (domNode?.children?.[0] as unknown as DataNode)?.data;
  const src = FIRST_DANCE_TRACKER_SRC[childData];

  if (!src) return null;

  return (
    <div className="mb-6">
      <FirstDanceTracker src={src} />
    </div>
  );
};
